import '../doc-sdk/src/init/log';
import log from 'loglevel';
import EventEmitter from 'event-emitter';
import {
  CONTAINER_TYPE,
  GRAPHIC,
  KV,
  ROLE_TYPE,
  SDK_CLIENT_TYPE,
  SDK_EVENT,
  SDK_PLAY_MODE,
  FORCE_DOC_TYPE,
  VERSION
} from './common/Constant';
import VhyReporter from '@vhallyun/log-reporter';
import Store from './common/Store';
import DocModuler from '../doc-sdk/src/DocModuler';
import OperateKit from './OperateKit';
import { InitializedAssert, SetCurrentDoc, verifyContainerType } from './utils/Decorator';
import { generateUUID, loadMsgScript, mixin } from './utils/utils';
import { streamVod } from './api/streamVod';
import Permission from './common/Permission';
import UndoManager from '../doc-sdk/src/doodle/manager/UndoManager';
import { generateKey, encodeRSA, decodeAES, setSecretKey, setSignType } from './shared/aut';

window.DocModuler = DocModuler;

// / 设置日志上报地址
VhyReporter.setConfig({ uploadUrl: process.env.REPORT_SERVER, __debug: parseInt(process.env.REPORT_DEBUG) });

// 撤销恢复步数
const UNDO_STEP_INFO = { undoStep: 0, redoStep: 0 };
/**
 * http://wiki.vhallops.com/pages/viewpage.action?pageId=60719114
 * http://wiki.vhallops.com/pages/viewpage.action?pageId=44663110
 *
 * @exports
 * @class Main
 */
export default class Main {
  constructor(opt, success = null, failed = null) {
    if (!opt) {
      const msg = { msg: 'opt 参数不是一个对象.' };
      failed && failed(msg);
      return;
    }

    // 先对基础数据进行存储以及检测
    let {
      channelId,
      appId,
      role,
      isVod,
      client,
      roomId = '',
      accountId,
      token,
      hide,
      mode,
      delay,
      isVodRecord = false,
      forceDocType = null,
      watermarkOption,
      context = {}
    } = opt;
    this.isVod = isVod;
    if (!isVod && !channelId) {
      const msg = { msg: `channelId 参数非法,channelId:${channelId}.` };
      failed && failed(msg);
      return;
    }

    if (isVod && !channelId) {
      channelId = '';
    }

    if (!appId || typeof appId !== 'string') {
      const msg = { msg: `appId参数非法, appId:${appId}` };
      failed && failed(msg);
      return;
    }

    // ROLE_TYPE 类型在1和4之间
    if (!role || typeof role !== 'number' || role < 1 || role > 4) {
      const msg = { msg: `role参数非法, role:${role}` };
      failed && failed(msg);
      return;
    }

    if (!accountId || typeof appId !== 'string') {
      const msg = { msg: `第三方用户Id不正确, accountId:${accountId}` };
      failed && failed(msg);
      return;
    }

    if (!client) {
      const msg = { msg: '需要传入client类型，类型值参考VHDocSDK.Client枚举' };
      failed && failed(msg);
      return;
    }

    if (!token) {
      const msg = { msg: 'token为空' };
      failed && failed(msg);
      return;
    }

    if (delay !== undefined && (delay < 0 || delay > 30)) {
      const msg = { msg: `delay参数非法，delay: ${delay}` };
      failed && failed(msg);
      return;
    }

    if (watermarkOption) {
      this.watermarkOption = watermarkOption;
    }

    let playMode = SDK_PLAY_MODE.FLV;
    if (mode) {
      playMode = mode;
    }

    let playDelay = -1;
    if (delay) {
      playDelay = delay;
    }
    this.context = context;
    EventEmitter(this);
    this.store = new Store();
    this.store.setKV(KV.CHANNEL_ID, channelId);
    this.store.setKV(KV.APP_ID, appId);
    this.store.setKV(KV.ROLE, ~~role);
    this.store.setKV(KV.IS_VOD, !!isVod);
    this.store.setKV(KV.CLIENT, client);
    this.store.setKV(KV.ROOM_ID, roomId);
    this.store.setKV(KV.THIRD_PARTY_USER_ID, accountId);
    this.store.setKV(KV.VFID, accountId);
    this.store.setKV(KV.ACCESS_TOKEN, token);
    this.store.setKV(KV.PLAY_MODE, playMode);
    this.store.setKV(KV.PLAY_DELAY, playDelay);
    this.store.setKV(KV.ISVODRECORD, !!isVodRecord);
    // 文档开关，默认为开
    this.store.setKV(KV.WATCH_ON, true);
    this.store.setKV(KV.FORCE_DOC_TYPE, forceDocType);
    this.permission = new Permission();
    this.permission.role = ~~role;
    this.store.setKV(KV.PERMISSION, this.permission.permission);

    // 以下这几个， 我也不知道干什么用的
    this.store.setKV(KV.PACKAGE_CHECK, 'sol');
    this.docMap = new Map();

    this.option = { stroke: '#000000', strokeWidth: 4 };
    this._currentDoc = null;
    this._forceV4 = !!opt.useV4;
    this._forceApiSend = !!opt.useApiSend;
    /**
     * 标示sdk实例是否全部加载完毕
     */
    this.isAllComplete = false;
    this.success = success;
    this.failed = failed;
    this.hide = !!hide;
    this.timer = null;

    // 注册共用上报参数
    VhyReporter.registerSuperProperties({
      channelId,
      appId,
      uid: accountId,
      roomId,
      version: process.env.VERSION
    });

    let data = {
      channelId,
      appId,
      roleType: role,
      isVod,
      client,
      accountId,
      hide,
      mode,
      delay,
      isVodRecord,
      forceDocType
    };
    VhyReporter.scout(260010, data);

    // 箭头撤销重做事件
    this.listenUndoStepEvent();
  }

  static createInstance(opts, success = null, failed = null) {
    let instance = new Main(opts, success, failed);
    setTimeout(() => {
      instance._init();
    }, 0);
    return instance;
  }

  /**
   *
   * @returns {Promise<void>}
   * @private
   */
  async _init() {
    const hasVhallMsg = typeof VhallMsg === 'undefined';
    if (hasVhallMsg) {
      const result = await loadMsgScript();
      // 加载失败;
      if (!result) {
        const msg = { msg: '加载消息库失败' };
        VhyReporter.scout(260011, msg);
        this.failed && this.failed(msg);
        return;
      }
    }

    let msgInfo = null;

    // 直播时 vhallMsg 进行socket链接
    if (!this.isVod) {
      const initVhallmsg = async () => {
        const option = {
          sdk: null,
          appId: this.store.getKV(KV.APP_ID),
          third_party_user_id: this.store.getKV(KV.THIRD_PARTY_USER_ID),
          token: this.store.getKV(KV.ACCESS_TOKEN),
          channelId: this.store.getKV(KV.CHANNEL_ID),
          context: this.context,
          hide: this.hide
        };
        // 在已加载v2.x.x的情况强制升级v1.x.x
        if (this._forceV4) {
          option.sdk = { name: 'vhall-jssdk-doc', version: process.env.VERSION };
        }

        // eslint-disable-next-line no-undef

        return new Promise((resolve, reject) => {
          VhallMsg.createInstance(
            option,
            msg => {
              log.debug('VhallMsg.createInstance');
              VhyReporter.scout(260122);
              resolve(msg);
            },
            err => {
              VhyReporter.scout(260011, err);
              this.failed && this.failed(err);
              reject(err);
            }
          );
        });
      };

      let vhallmsg = await initVhallmsg();
      log.debug('vhallmsg.connect');
      // is v2.x.x msg api
      if (vhallmsg.sdkName) {
        try {
          await vhallmsg.join();
        } catch (e) {
          // 加入房间失败
          VhyReporter.scout(260011, e);
          this.failed && this.failed(e);
          if (vhallmsg && vhallmsg.destroy) vhallmsg.destroy();
          return;
        }
      } else {
        // 定义消息的一些事件，透传处理，文档融屏会用到
        vhallmsg.onOnline = () => {
          this.emit(SDK_EVENT.WS_ONLINE);
        };
        vhallmsg.onOffline = () => {
          this.emit(SDK_EVENT.WS_OFFLINE);
        };
        vhallmsg.onDisconnect = reason => {
          this.emit(SDK_EVENT.WS_DISCONNECT, reason);
        };
        vhallmsg.onReconnectAttempt = attempt => {
          this.emit(SDK_EVENT.WS_RECONNECT_ATTEMPT, attempt);
        };
        // 建立消息连接
        vhallmsg.connect();
      }
      this.store.setKV(KV.SOCKET_INSTANCE, vhallmsg);
      // get doc server_info
      msgInfo = vhallmsg.info;

      if (!this.kit) {
        log.debug('new OperateKit');
        this.kit = new OperateKit(this);
      }

      const rnd = generateKey(48); // 生成随机字符串
      const random_key = encodeRSA(rnd, process.env.PUBLIC_KEY); // RSA加密
      // 注意: 如果公钥不合法，random_key得到的值是false
      log.info('random_key:', random_key);
      // API请求
      const rsData = await this.kit.init({ channelId: this.store.getKV(KV.CHANNEL_ID), random_key, from: 'jssdk-doc' });
      // log.info('rsData:', JSON.stringify(rsData));
      if (rsData?.secret_key) {
        // AES解密
        const secretKey = decodeAES(rsData.secret_key, rnd.substring(0, 32), rnd.substring(32, 48));
        setSignType(rsData?.sign_type); // 存储签名类型，0-MD5 2-SHA256 3-SM3，默认2
        setSecretKey(secretKey); // 存储秘钥
      }
      if (!msgInfo && rsData) {
        msgInfo = rsData;
      }
    } else {
      if (!this.kit) {
        log.debug('new OperateKit');
        this.kit = new OperateKit(this);
      }

      log.debug('createBoard');
      const myVodNode = document.createElement('div');
      const elId = 'myVodNode';
      myVodNode.setAttribute('id', elId);
      document.body.appendChild(myVodNode);
      this.createBoard({
        elId: elId,
        width: 1,
        height: 1,
        id: elId,
        isVod: true
      });
      log.debug('createBoard end');
      if (this.store.getKV(KV.ISVODRECORD)) mixin(this, streamVod, this);
    }

    const document_server = msgInfo?.document_server || process.env.DEFAULT_DOCUMENT_SERVER;
    this.store.setKV(KV.DOCUMENT_SERVER, document_server + '/');

    VhyReporter.scout(260123);
    this.success && this.success();
  }

  /**
   * 创建文档容器
   *
   * @param {*} opts
   * @memberof Main
   * @returns 创建成功返回容器实例， 否则返回null
   */
  async createDocument(opts) {
    let { elId, width, height, id, noDispatch, docId, option = {}, docType, enableRetinaScaling = false } = opts;
    if (!elId || !width || !height)
      return Promise.reject({ msg: `必要参数为空，创建文档失败,elId:${elId}, width:${width}, height:${height}` });
    if (!this.checkOption(option)) return Promise.reject({ msg: 'option参数校验失败' });
    this.isAllComplete = false;
    // 实例id，通常为elId
    if (!id) id = elId;

    VhyReporter.scout(260125, {
      doc_cid: id || '',
      elId,
      width,
      height,
      noDispatch,
      docId: docId || '',
      roleType: this.permission.role
    });

    const doc = new DocModuler();
    const passThrough = {
      userId: this.store.getKV(KV.THIRD_PARTY_USER_ID),
      docId
    };
    doc.passThrough(passThrough);
    const { editable, pageable } = this.permission.permission;
    const isVod = this.store.getKV(KV.IS_VOD);

    doc.registMessager(this.kit.sendMsg.bind(this));
    doc.on('slideChange', this.kit.reportChapters.bind(this));
    doc.on('stepChange', this.kit.reportChapters.bind(this));
    // 文档缩放比触发事件
    doc.on('scaleTrigger', info => {
      this.emit(SDK_EVENT.SCALE_TRIGGER, info);
    });
    doc.on('docNotExit', res => {
      this.emit(SDK_EVENT.DOCUMENT_NOT_EXIT, res);
      const err = new Error('docNotExit');
      err.$preset = { doc_cid: res && (res.cid || res.id), docId: res && res.docId };
    });
    doc.on('playbackComplete', res => {
      this.emit(SDK_EVENT.PLAYBACKCOMPLETE, res);
      VhyReporter.scout(260161, { doc_cid: doc.id || doc.cid, docId: res && res.docId });
    });
    doc.on('animationInfo', ({ cid, params: { animationInfo, docId } }) => {
      doc.store.setKV('animationInfo', new Map(animationInfo));
    });
    doc.on('documentOnload', ev => {
      // doc_id
      VhyReporter.scout(260162, { doc_cid: ev && (ev.cid || ev.id) });
      doc.loadComplete = true;
      let isAllComplete = true;
      this.docMap.forEach(item => {
        !item.loadComplete && (isAllComplete = false);
      });
      if (isAllComplete) {
        this.isAllComplete = true;
        this.emit(SDK_EVENT.ALL_COMPLETE, {});
        if (this.permission.role == ROLE_TYPE.HOST || this.permission.role == ROLE_TYPE.ASSISTANT) {
          this.resetUndoManagerData();
        }
        VhyReporter.scout(260163);
        console.log('ALL_COMPLETE:', isAllComplete, this.isAllComplete);
      }
      /** 检查是否需要请求章节信息 */
      !this.store.getKV(KV.IS_VOD) && !doc.store.getKV(KV.CHAPTERS) && this.getChapters({ elId, docId: doc.docId });

      /** 是否需要设置默认画笔类型 */
      const { stroke, strokeWidth, graphicType } = { graphicType: -1, ...this.option, ...option };
      doc.setGraphic(graphicType);
      doc.option = { stroke, strokeWidth };
    });
    doc.on('report_page_change', this.kit.reportPageChange.bind(this));
    doc.on('documentReady', e => {
      const id = (this.currentDoc && this.currentDoc.id) || '';
      const obj = {
        docId,
        info: e,
        elId: id
      };
      this.emit(SDK_EVENT.DOCUMENT_LOAD_COMPLETE, obj);
      VhyReporter.scout(260164, { doc_cid: id, docId });
    });
    const urlBase = this.store.getKV(KV.DOCUMENT_SERVER);
    const initOpt = {
      basic: {
        width,
        height,
        urlBase,
        enableRetinaScaling
      },
      container: {
        editable,
        pageable,
        cacheable: false
      }
    };
    doc.createDocument(elId, id, initOpt);
    doc.isVod = isVod;
    if (isVod && doc.vodIsUseCompat()) doc.vodSetData(this.vodData || []);
    this.docMap.set(id, doc);

    doc.setWatermark();

    await this.kit.createDocument({ id, width, height, noDispatch, docId, docType });
    return Promise.resolve();
  }

  /**
   * 创建白板容器
   *
   * @param {*} opts
   * @returns 创建成功返回容器实例， 否则返回null
   */
  async createBoard(opts) {
    log.debug('进入createBoard函数');
    let { elId, width, height, backgroundColor, id, noDispatch, option = {}, enableRetinaScaling = false } = opts;
    if (!elId || !width || !height)
      return Promise.reject({
        msg: `必要参数为空，创建白板失败,elId:${elId}, width:${width}, height:${height}, backgroundColor:${backgroundColor}`
      });
    if (!this.checkOption(option)) return Promise.reject({ msg: 'option参数校验失败' });
    this.isAllComplete = false;
    // 实例id，通常为elId
    if (!id) {
      id = elId;
    }
    VhyReporter.scout(260127, {
      doc_cid: id || '',
      width,
      height,
      backgroundColor,
      noDispatch,
      params: option,
      roleType: this.permission.role
    });
    log.debug('new DocModuler');
    const board = new DocModuler();
    log.debug('new DocModuler end');
    const passThrough = {
      userId: this.store.getKV(KV.THIRD_PARTY_USER_ID),
      docId: ''
    };
    log.debug('board.passThrough');
    board.passThrough(passThrough);

    const { editable } = this.permission.permission;
    const isVod = this.store.getKV(KV.IS_VOD);

    log.debug('board.registMessager');
    board.registMessager(this.kit.sendMsg.bind(this));
    log.debug('board.registMessager end');
    // 白板缩放比触发事件
    board.on('scaleTrigger', res => {
      this.emit(SDK_EVENT.SCALE_TRIGGER, res);
    });
    const urlBase = this.store.getKV(KV.DOCUMENT_SERVER);
    const initOpt = {
      basic: {
        width,
        height,
        backgroundColor,
        urlBase,
        enableRetinaScaling
      },
      container: {
        editable,
        cacheable: false
      }
    };
    log.debug('board.createBoard');
    board.createBoard(elId, id, initOpt);
    log.debug('board.createBoard end');
    board.isVod = isVod;
    if (isVod && board.vodIsUseCompat()) board.vodSetData(this.vodData || []);
    this.docMap.set(id, board);

    if (opts.width != 1 && opts.height != 1) {
      board.setWatermark();
    }

    await this.kit.createBoard({ id, width, height, backgroundColor, noDispatch });
    const { stroke, strokeWidth, graphicType } = { graphicType: -1, ...this.option, ...option };
    board.setGraphic(graphicType);
    board.option = { stroke, strokeWidth };
    board.store.setKV('backgroundColor', backgroundColor);
    /**
     * 检查其余实例是否加载完成，为了配合doc的 全部加载完成事件,为了兼容 setRemoteData带来的 问题，只能延迟 1.5s 再查看，否则会造成多实例第一个是白板的情况下直接报加载完成事件的bug
     */
    await this._delay(1500);
    board.loadComplete = true;
    let isAllComplete = true;
    this.docMap.forEach(item => {
      !item.loadComplete && (isAllComplete = false);
    });
    if (isAllComplete) {
      this.isAllComplete = true;
      this.emit(SDK_EVENT.ALL_COMPLETE, {});
      if (this.permission.role == ROLE_TYPE.HOST || this.permission.role == ROLE_TYPE.ASSISTANT) {
        this.resetUndoManagerData();
      }
    }
    log.debug('Main createBoard end');
    return Promise.resolve();
  }

  /**
   * 销毁容器
   *
   * @param {*} opts
   * @memberof Main
   * 销毁时判断是否销毁的是已经激活的文档
   */
  async destroyContainer(opts = {}) {
    const { id } = opts;
    if (!id) {
      log.warn('容器id为空，无法销毁');
      return Promise.reject({ msg: '容器id为空，无法销毁' });
    }
    VhyReporter.scout(260129, { doc_cid: id || '' });
    const doc = this.docMap.get(id);
    if (!doc) return Promise.resolve();
    const docId = doc.docId || '';
    doc.destroy();
    this.docMap.delete(id);
    try {
      if (this._currentDoc.store.getKV('cid') == id) {
        this._currentDoc = null;
        this.store.setKV(KV.CURRENT_DOC_CID, -1);
      }
    } catch (error) {
      console.log(error);
    }
    /** 防止被销毁的文档还未加载完成，其余文档加载完成导致未派发所有文档加载完成的问题 */
    let isAllComplete = true;
    this.docMap.forEach(item => {
      !item.loadComplete && (isAllComplete = false);
    });
    if (isAllComplete && [...this.docMap.keys()].length) {
      this.isAllComplete = true;
      console.log('ALL_COMPLETE:', isAllComplete, this.isAllComplete);
    }
    this.kit.destroy({ id, docId });
    return Promise.resolve();
  }

  /**
   * 加载文档
   *
   * @param {*} opts
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  @verifyContainerType(CONTAINER_TYPE.DOCUMENT)
  loadDoc(opts) {
    const { docId, docType, id } = opts;
    // 根据docId 加载文档信息
    const forceDocType = this.store.getKV(KV.FORCE_DOC_TYPE);
    if (
      (forceDocType === FORCE_DOC_TYPE.DYNAMIC || forceDocType === FORCE_DOC_TYPE.STATIC) &&
      docType !== forceDocType
    ) {
      const err = { code: 10002, msg: '强制演示类型与预演式类型不相符' };
      return Promise.reject(err);
    }
    VhyReporter.scout(260134, { docId, docType, doc_cid: id || '' });
    this.store.setKV(KV.DOC_ID, docId);
    return new Promise((resolve, reject) => {
      this.kit
        .getDocInfo({ docId })
        .then(async res => {
          const { hash, doc_type, status, status_jpeg } = res;
          if ((doc_type == 1 && status != 200) || (doc_type == 2 && status_jpeg != 200)) {
            reject({ code: 10002, msg: '文档转换尚未完成' });
            return;
          }
          switch (docType) {
            case 1:
              if (status != 200) {
                reject({ code: 10002, msg: '文档转换尚未完成或指定转码类型与欲演示类型不相符' });
                return;
              }
              // 用户想演示动态文档
              res.slidesTotal = res.converted_page;
              res.doc_type = docType;
              break;
            case 2:
              if (status_jpeg != 200) {
                reject({ code: 10002, msg: '文档转换尚未完成或指定转码类型与欲演示类型不相符' });
                return;
              }
              // 用户想演示静态文档
              res.slidesTotal = res.converted_page_jpeg;
              res.doc_type = docType;
              break;
            default:
              // 兼容老版本，不传 docType 的时候
              // 根据返回的doc_type决定用哪个文档
              res.slidesTotal = doc_type == 1 ? res.converted_page : res.converted_page_jpeg;
              break;
          }

          res.slideIndex = 0;
          res.stepIndex = 0;
          this.currentDoc.docId = docId;
          this.currentDoc.setDoc(hash, JSON.stringify(res));
          // await this.getChapters({ elId: id, docId });
          resolve(res);
        })
        .catch(rej => {
          log.error(rej);
          reject(rej);
          const errData = {};
          this.docMap.forEach(item => {
            const tmp = item.store.getKV('passThrough');
            if (tmp.docId == docId) {
              errData.docId = docId;
              errData.cid = item.store.getKV('cid');
            }
          });
          this.emit(SDK_EVENT.DOCUMENT_NOT_EXIT, errData);
          const err = new Error('docNotExit');
          err.$preset = { doc_cid: errData.cid, docId: docId };
        });
    });
  }
  /** 获取文档章节信息 */
  async getChapters({ elId, docId }) {
    try {
      const doc = this.docMap.get(elId);
      const chapters = await this.kit.getChapters(docId);
      doc.store.setKV(KV.CHAPTERS, chapters);
      const result = [];
      for (const { page, title, step, subsection, created_at, cid, document_id } of chapters) {
        const sub = subsection.map(({ page, title, step, created_at }) => {
          return { slideIndex: page, stepIndex: step, title, createTime: created_at };
        });
        result.push({
          slideIndex: page,
          stepIndex: step,
          title,
          sub,
          cid: cid || elId,
          createTime: created_at,
          docId: document_id
        });
      }
      return Promise.resolve({ chapters: result, elId, docId });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @SetCurrentDoc()
  @InitializedAssert()
  @verifyContainerType(CONTAINER_TYPE.DOCUMENT)
  setDoc(opts, hash, docOpts) {
    // VhyReporter.scout(260130, { hash, opts, docOpts });
    this.currentDoc.setDoc(hash, docOpts);
  }

  /**
   *
   *
   * @param {*} opts
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  @verifyContainerType(CONTAINER_TYPE.DOCUMENT)
  prevPage(opts) {
    VhyReporter.scout(260140, { roleType: this.permission.role });
    this.currentDoc.prevPage();
  }

  /**
   *
   *
   * @param {*} opts
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  @verifyContainerType(CONTAINER_TYPE.DOCUMENT)
  nextPage(opts) {
    VhyReporter.scout(260141, { roleType: this.permission.role });
    this.currentDoc.nextPage();
  }

  /**
   *
   *
   * @param {*} opts
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  @verifyContainerType(CONTAINER_TYPE.DOCUMENT)
  gotoPage(opts) {
    const { page: to } = opts;
    VhyReporter.scout(260144, { page: to, roleType: this.permission.role });
    this.currentDoc.gotoPage(to);
  }

  /**
   *
   *
   * @param {*} opts
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  @verifyContainerType(CONTAINER_TYPE.DOCUMENT)
  prevStep(opts) {
    VhyReporter.scout(260142, { roleType: this.permission.role });
    this.currentDoc.prevStep();
  }

  /**
   * 下一步
   *
   * @memberOf Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  @verifyContainerType(CONTAINER_TYPE.DOCUMENT)
  nextStep() {
    VhyReporter.scout(260143, { roleType: this.permission.role });
    this.currentDoc.nextStep();
  }

  /**
   * 跳转到某一页的某一步
   *
   * @memberOf Main
   * @param opts
   */
  @SetCurrentDoc()
  @InitializedAssert()
  @verifyContainerType(CONTAINER_TYPE.DOCUMENT)
  gotoStep(opts) {
    const { pageId, stepId } = opts;
    VhyReporter.scout(260145, { pageId, stepId, roleType: this.permission.role });
    this.currentDoc.gotoStep(pageId, stepId);
  }

  /**
   * 设置画笔
   *
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setPen() {
    this.currentDoc.setGraphic(GRAPHIC.PEN);
  }

  /**
   * 设置荧光笔
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setHighlighters() {
    this.currentDoc.setGraphic(GRAPHIC.NITE_WHITE_PEN);
  }

  /**
   * 设置四边形
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setSquare() {
    this.currentDoc.setGraphic(GRAPHIC.SQUARE);
  }

  /**
   * 设置圆形
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setCircle() {
    this.currentDoc.setGraphic(GRAPHIC.CIRCLE);
  }

  /**
   * 设置图片，需要在初始化的时候设置uploadURL
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setBitmap() {
    this.currentDoc.setGraphic(GRAPHIC.BITMAP);
  }

  /**
   * 设置等腰三角形
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setIsoscelesTriangle() {
    this.currentDoc.setGraphic(GRAPHIC.ISOSCELES_TRIANGLE);
  }

  /**
   * 设置直角三角形
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setRightTriangle() {
    this.currentDoc.setGraphic(GRAPHIC.RIGHT_TRIANGLE);
  }

  /**
   * 设置单箭头
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setSingleArrow() {
    this.currentDoc.setGraphic(GRAPHIC.SINGLE_ARROW);
  }

  /**
   * 设置双箭头
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setDoubleArrow() {
    this.currentDoc.setGraphic(GRAPHIC.DOUBLE_ARROW);
  }

  /**
   * 设置直线
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setLine() {
    this.currentDoc.setGraphic(GRAPHIC.LINE);
  }

  /**
   * 设置文字工具
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setText() {
    this.currentDoc.setGraphic(GRAPHIC.TEXT);
  }

  /**
   * 取消画笔
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  cancelDrawable() {
    this.currentDoc.setGraphic(GRAPHIC.CANCEL);
  }

  /**
   * 清空画布
   *
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  clear() {
    this.currentDoc.setTool(5);
    this.resetUndoManagerData();
  }

  /**
   * 设置画笔颜色
   *
   * @param {*} opts
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setStroke(opts = {}) {
    const { color } = opts;
    this.currentDoc.option = { stroke: color };
  }

  /**
   * 获取画笔颜色
   *
   * @returns
   * @memberof Main
   */
  getStroke() {
    return this.option.stroke;
  }

  /**
   * 设置画笔粗细
   *
   * @param {*} opts
   * @memberof Main
   */
  @SetCurrentDoc()
  @InitializedAssert()
  setStrokeWidth(opts = {}) {
    const { width } = opts;
    this.currentDoc.option = { strokeWidth: width };
  }

  /**
   * 获取画笔粗细
   *
   * @returns {number}
   */
  getStrokeWidth() {
    return this.option.strokeWidth;
  }

  setControlStyle(opts = {}) {
    const { style } = opts;
    if (!style) {
      log.warn('设置样式需要填写样式，传参数 {style: { 样式代码 }}');
      return;
    }
    if (opts && opts.id) {
      const { id } = opts;
      const doc = this.docMap.get(id);
      if (doc) {
        doc.setControlStyle(style);
      }
    } else {
      this.docMap.forEach(doc => {
        doc.setControlStyle(style);
      });
    }
  }

  /**
   * 获取缩略图的url
   *
   * @memberof Main
   * @returns [] 缩略图的image路径
   */
  getThumbnailList(opts) {
    const arr = [];
    if (opts && opts.id) {
      const { id } = opts;
      const doc = this.docMap.get(id);
      if (doc) {
        const obj = {};
        obj.id = doc.id;
        obj.list = doc.getThumbnailList();
        arr.push(obj);
      }
      return arr;
    }

    this.docMap.forEach(item => {
      if (item.container.type === 'document') {
        const obj = {};
        obj.id = item.id;
        obj.list = item.getThumbnailList();
        arr.push(obj);
      }
    });

    return arr;
  }

  getDocInfos(opts) {
    const arr = [];
    if (opts && opts.id) {
      const { id } = opts;
      const doc = this.docMap.get(id);
      if (doc.container && doc.container.type === 'document') {
        const obj = {};
        obj.id = doc.id;
        obj.list = doc.getDocInfo();
        arr.push(obj);
      }
      return arr;
    }

    this.docMap.forEach(item => {
      if (item.container.type === 'document') {
        const obj = {};
        obj.id = item.id;
        obj.list = item.getDocInfo();
        arr.push(obj);
      }
    });

    return arr;
  }

  setSize(w, h, opts) {
    VhyReporter.scout(260132, { w, h, opts });
    if (!(w > 0 && h > 0)) {
      log.warn('setSize宽或高必须大于0');
      return;
    }

    if (!opts || !opts.id) {
      this.docMap.forEach(doc => {
        doc.setSize(w, h);
      });
    } else {
      const doc = this.docMap.get(opts.id);
      if (!doc) {
        log.error(`doesn't exist doc, id:${opts.id}`);
        return;
      }

      doc.setSize(w, h);
    }
  }
  /**
   * 橡皮擦
   */
  @SetCurrentDoc()
  setEraser() {
    this.currentDoc.setTool(2);
  }
  /**
   * 撤销
   */
  @SetCurrentDoc()
  undo() {
    if (UNDO_STEP_INFO.undoStep === 0) return;
    this.currentDoc.setTool(3);
  }
  /**
   * 恢复
   */
  @SetCurrentDoc()
  redo() {
    if (UNDO_STEP_INFO.redoStep === 0) return;
    this.currentDoc.setTool(4);
  }

  @SetCurrentDoc()
  @InitializedAssert()
  zoomIn() {
    this.currentDoc.zoomIn();
  }

  @SetCurrentDoc()
  @InitializedAssert()
  zoomOut() {
    this.currentDoc.zoomOut();
  }

  @SetCurrentDoc()
  @InitializedAssert()
  zoomReset() {
    this.currentDoc.zoomReset();
  }

  @SetCurrentDoc()
  @InitializedAssert()
  move() {
    this.currentDoc.zoomMove();
  }

  @SetCurrentDoc()
  @InitializedAssert()
  cancelZoom() {
    const { permission } = this.permission;
    this.currentDoc.zoomEnd(permission);
  }

  /**
   * 销毁所有
   * @param {*} destroyMsg 销毁实例时可选择是否退出聊天频道
   * =true 退出
   * =false 不退出
   */
  destroy(destroyMsg = true) {
    VhyReporter.scout(260124);
    if (this.docMap) {
      this.docMap.forEach(item => {
        item.destroy();
      });
      this.docMap.clear();
    }

    let msg = this.store.getKV(KV.SOCKET_INSTANCE);
    if (msg) {
      this.store.deleteKey(KV.SOCKET_INSTANCE);
      if (destroyMsg) {
        msg.destroy();
        msg = null;
      }
    }
    this.__ee__ = [];
    if (this.kit) {
      this.kit.destroyKit();
      this.kit = null;
    }
    VhallMsg && VhallMsg.__ee__ && (VhallMsg.__ee__.broadcast = []);
  }

  setVodData(opts, chapters = []) {
    log.debug('Main setVodData');
    const { data } = opts;
    this.vodData = data;
    const doc = this.docMap.get('myVodNode');
    doc.vodSetData(data);
    const chapterLength = chapters && chapters.length;
    const cuepointLength = data && data.length;
    const cids = this.getVodAllCids().map(info => info && info.cid);
    VhyReporter.scout(260165, { chapterLength, cuepointLength, cids });
    const result = [];
    for (const { page, title, step, subsection, created_at, cid, document_id } of chapters) {
      const sub = subsection.map(({ page, title, step, created_at }) => {
        return { slideIndex: page, stepIndex: step, title, createTime: created_at };
      });
      result.push({ slideIndex: page, stepIndex: step, title, sub, cid, createTime: created_at, docId: document_id });
    }

    this.emit(SDK_EVENT.VOD_CUEPOINT_LOAD_COMPLETE, { chapters: result });
  }

  /** 遍历回放数据，筛选出每一个文档容器的首次加载iframe数据 */
  loadVodIframe() {
    let cids = {};
    /** part1. 筛选出文档的容器 */
    for (const iterator of this.getVodAllCids()) {
      if (iterator.cid.indexOf('document') !== -1) {
        cids[iterator.cid] = '';
      }
    }
    if (!Object.keys(cids).length) return;
    /** part2. 筛选出文档的容器对应的第一条翻页/翻步数据并load */
    for (const iterator of this.vodData) {
      const content2 = JSON.parse(iterator.content2);
      if (content2.command[0].event == 'onStepChange' || content2.command[0].event == 'onSlideChange') {
        if (cids[content2.cid] === '') {
          cids[content2.cid] = iterator.content2;
          const doc = this.docMap.get(content2.cid);
          doc._ee.dispatch('vod_event', [iterator.content2]);
          let needBreak = true;
          for (const item of Object.keys(cids)) {
            !cids[item] && (needBreak = false);
          }
          if (needBreak) break;
        }
      }
    }
  }

  /**
   *
   * @param time
   */
  setVodTime(time) {
    log.debug('Main setVodTime');
    const obj = {};
    // 查询文档显示
    obj.container = [];
    const cidInfos = this.getVodAllCids();
    const containers = [];
    cidInfos.forEach(info => {
      if (time > info.create && time < info.destroy) {
        log.debug(`push: ${info.cid}, time: ${time}, create: ${info.create}, destroy: ${info.destroy}`);
        containers.push(info.cid);
      }
    });
    obj.container = containers;

    // 查询观看端是否演示
    const watchStatuses = this.getVodAllWatchStatus();
    let status = false;
    watchStatuses.forEach(statusInfo => {
      if (time > statusInfo.createTime) {
        status = statusInfo.open;
      }
    });
    obj.watchOpen = status;

    // 查询当前激活哪个容器
    const activatedMap = this.getAllActivatedCids();
    let activatedCid = '';
    activatedMap.forEach(item => {
      if (time > item.activatedTime) {
        activatedCid = item.cid;
      }
    });
    obj.activeId = activatedCid;
    log.debug(`activeId: ${obj.activeId}`);
    this.emit(SDK_EVENT.VOD_TIME_UPDATE, obj);

    // 查询回放画笔等信息
    const doc = this.docMap.get(activatedCid);
    if (doc) {
      doc.vodTime(time);
    }
  }

  getVodAllCids() {
    // let ids = this.allContainerId;
    const myVodNodeId = 'myVodNode';
    const doc = this.docMap.get(myVodNodeId);
    if (doc) {
      return doc.vodAllCids();
    }

    return [];
  }

  getLiveAllCids() {
    return new Array(...this.docMap.keys());
  }

  getAllActivatedCids() {
    log.debug('call getAllActivatedCids');
    // 查询回放画笔等信息
    const myVodNodeId = 'myVodNode';
    const doc = this.docMap.get(myVodNodeId);
    if (doc) {
      return doc.vodAllActivatedCids();
    }

    return [];
  }

  getVodAllWatchStatus() {
    log.debug('call getVodAllWatchStatus');
    const myVodNodeId = 'myVodNode';
    const doc = this.docMap.get(myVodNodeId);
    if (doc) {
      return doc.vodAllWatchStatus();
    }

    return [];
  }

  /**
   * 设置权限
   */
  setRole(roleType) {
    VhyReporter.scout(260133, { roleType });
    return new Promise((resolve, reject) => {
      const { pageable } = this.permission.permission;
      if (pageable && !this.isAllComplete && [...this.docMap.keys()].length) {
        reject({ code: 10001, msg: '实例未全部加载完毕，切换失败' });
        return;
      }

      this.store.setKV(KV.ROLE, roleType);
      this.permission.role = roleType;
      const { permission } = this.permission;
      this.docMap.forEach(doc => {
        const container = doc.container;
        if (!container) {
          return;
        }

        if (container.type === 'document') {
          container.pageable = permission.pageable;
        }
        container.editable = permission.editable;
        // doc.zoomable = zoomable
        container.cacheable = permission.cacheable;
      });
      console.log('this.docMap', this.docMap);
      resolve();
    });
  }

  getRole() {
    return this.store.getKV(KV.ROLE);
  }

  async setData(opts) {
    // 某些情况下，翻页消息可能比创建消息先到，如果不等待创建消息先执行，文档容器不会创建，文档区域显示为空。
    let doc = this.docMap.get(opts.cid);
    if (!doc) {
      doc = await this.checkDocByCid(opts.cid);
    }
    doc && doc.setData(JSON.stringify(opts));
  }

  setRemoteData(data) {
    let { cid, docId, active, pageData, hash, is_board, doc_type } = data;
    const forceDocType = this.store.getKV(KV.FORCE_DOC_TYPE);
    if (forceDocType === FORCE_DOC_TYPE.DYNAMIC || forceDocType === FORCE_DOC_TYPE.STATIC) {
      doc_type = forceDocType;
      data.doc_type = forceDocType;
    }

    const doc = this.docMap.get(cid);
    if (!doc) {
      return;
    }

    if (active === 1) {
      this._currentDoc = doc;
      this.currentDoc.setSelected(true);
      this.store.setKV(KV.CURRENT_DOC_CID, cid);
    }

    // 文档
    if (is_board === 1) {
      data.slidesTotal = doc_type == 1 ? data.converted_page : data.converted_page_jpeg;
      data.slideIndex = data.show_page;
      data.stepIndex = data.show_step;
      doc.docId = docId;
      doc.setDoc(hash, JSON.stringify(data), true);
      // doc.setAllData(pageData)
    } else {
      pageData.forEach(data => {
        doc.setData(JSON.stringify(data));
      });
    }
  }

  setRemoteData2(list) {
    list.forEach(item => {
      this.setRemoteData(item);
    });
  }

  /**
   * 设置选中容器
   * 如果找到了对应id的容器，返回容器，否则返回null
   *
   * @param {*} opts
   * @memberof Main
   * @returns
   */
  async selectContainer({ id, noDispatch = false }) {
    const selectedId = this.store.getKV(KV.CURRENT_DOC_CID);
    if (selectedId === id) return Promise.resolve();
    if (this._currentDoc) {
      this._currentDoc.setSelected(false);
      this._currentDoc = null;
    }
    const doc = this.docMap.get(id);
    if (!doc) {
      log.error(`不存在id为${id}的容器`);
      return Promise.reject({ msg: `不存在id为${id}的容器` });
    }

    this._currentDoc = doc;
    this._currentDoc.setSelected(true);
    this.store.setKV(KV.CURRENT_DOC_CID, id);
    VhyReporter.scout(260146, { docCid: id || '', noDispatch });
    if (!noDispatch) {
      await this._delay(150);
      await this.kit.select(id, {
        cw: doc.store.getKV('cw'),
        ch: doc.store.getKV('ch'),
        width: doc.store.getKV('imageWidth'),
        height: doc.store.getKV('imageHeight'),
        backgroundColor: doc.store.getKV('backgroundColor') || null,
        docId: doc.docId
      });
    }
    return Promise.resolve();
  }

  /**
   * 开启观看端显示
   *
   * @param {*} opts
   * @memberof Main
   */
  switchOnContainer(opts = null) {
    let id = '';
    if (opts) {
      id = opts.id;
    }
    this.store.setKV(KV.WATCH_ON, true);
    this.kit.switchOn(id);
  }

  /**
   * 关闭观看端演示
   *
   * @param {*} opts
   * @memberof Main
   */
  switchOffContainer(opts = null) {
    let id = '';
    if (opts) {
      id = opts.id;
    }
    this.store.setKV(KV.WATCH_ON, false);
    this.kit.switchOff(id);
  }

  /**
   * 获取当前appId, channelId 下的所有容器列表
   *
   * @returns
   * @memberof Main
   */
  getContainerInfo(opts = null) {
    return this.kit.getContainerInfo(opts);
  }

  resetContainer() {
    this.docMap.forEach(doc => {
      doc.destroy();
    });
    this.docMap.clear();
    this.kit.resetContainerInfo();
  }

  setPlayMode(val) {
    VhyReporter.scout(260150, { playMode: val });
    const modes = Object.values(SDK_PLAY_MODE);
    if (modes.indexOf(val) == -1) {
      log.warn(`不支持模式:${val}`);
      return;
    }
    this.store.setKV(KV.PLAY_MODE, val);
  }

  /**
   * 设置accountId
   * 内部使用，用于转播功能
   * @param {object} opt
   * 该方法废弃
   */
  setAccountId(isRelay) {
    this.setRelay(isRelay);
  }
  /**
   * 设置转播时，send接口增加参数 is_cache 禁止后端缓存消息，或直接本地禁止发送消息
   */
  setRelay(isRelay = true) {
    VhyReporter.scout(260151, { isRelay });
    this.store.setKV(KV.ISRELAY, isRelay);
  }

  start(state, type) {
    VhyReporter.scout(260152, { state, type, roleType: this.permission.role });
    if (this.authentication('start')) {
      return this.kit.start(state, type);
    }
    return Promise.resolve();
  }

  republish() {
    VhyReporter.scout(260153, { roleType: this.permission.role });
    if (this.authentication('republish') && !this.timer) {
      this.timer = setTimeout(() => {
        this.timer = null;
      }, 1000);
      return this.kit.republish();
    }
    return Promise.resolve();
  }
  authentication(text) {
    const { pageable, editable } = this.permission.permission;
    if (!pageable && !editable) {
      log.warn(`该用户无编辑/翻页权限，不应调用 ${text} 方法`);
      return false;
    }
    if (this.isVod) {
      log.warn(`回放不应调用 ${text} 方法`);
      return false;
    }
    return true;
  }

  checkOption(option) {
    const { stroke, strokeWidth, graphicType } = option;
    if (stroke && typeof stroke !== 'string') {
      const msg = { msg: `option 下的 stroke 参数应为 string，现传入 stroke 类型为 ${typeof stroke}` };
      log.error(msg);
      return false;
    }
    if (strokeWidth && typeof strokeWidth !== 'number') {
      const msg = { msg: `option 下的 strokeWidth 参数应为 number，现传入 strokeWidth 类型为: ${typeof strokeWidth}` };
      log.error(msg);
      return false;
    }
    if (graphicType && typeof graphicType !== 'number') {
      const msg = { msg: `option 下的 graphicType 参数应为 number，现传入 graphicType 类型为: ${typeof graphicType}` };
      log.error(msg);
      return false;
    }
    return true;
  }

  _delay(time) {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve();
      }, time);
    });
  }
  /**
   * 设置权限token
   * @param {}} token
   */
  setToken(token) {
    this.store.setKV(KV.ACCESS_TOKEN, token);
  }

  get currentDoc() {
    return this._currentDoc;
  }

  get allContainerId() {
    return Array.from(this.docMap.keys());
  }

  get allContainer() {
    return Array.from(this.docMap.values());
  }

  get zoomFactors() {
    return DocModuler.zoomFactors;
  }

  set zoomFactors(value) {
    DocModuler.zoomFactors = value;
  }
  get watermarkOption() {
    return DocModuler.watermarkOption;
  }

  set watermarkOption(option) {
    DocModuler.watermarkOption = option;
  }

  /**
   * 创建水印
   * 在水印区域大小有变化时，可以直接调用 setWatermark(true) 重新生成
   * @param {object} option 水印选项, 不设置此项，默认会使用先前设置的 DocModuler.watermarkOption 参数
   * @param {boolean} always 每次重新生成水印图片，不缓存，默认false。
   * @returns
   */
  setWatermark(option, always) {
    this.docMap.forEach(doc => {
      if (doc.id != 'myVodNode') {
        doc.setWatermark(option, always);
      }
    });
  }

  /**
   * 清空水印
   */
  clearWatermark() {
    this.docMap.forEach(doc => {
      if (doc.id != 'myVodNode') {
        doc.clearWatermark();
      }
    });
  }

  static get Event() {
    return SDK_EVENT;
  }

  static get Client() {
    return SDK_CLIENT_TYPE;
  }

  static get RoleType() {
    return ROLE_TYPE;
  }

  static get PlayMode() {
    return SDK_PLAY_MODE;
  }

  static get GRAPHIC() {
    return GRAPHIC;
  }

  static get forceDocType() {
    return FORCE_DOC_TYPE;
  }

  static get VERSION() {
    return VERSION;
  }

  createUUID(prefix = 'document') {
    return `${prefix}-${generateUUID()}`;
  }
  /**
   * 撤销重做事件监听
   */
  listenUndoStepEvent() {
    UndoManager.getInstance().on('undo_step_change', data => {
      UNDO_STEP_INFO.undoStep = data.undoStep;
      UNDO_STEP_INFO.redoStep = data.redoStep;
      this.emit(SDK_EVENT.UNDO_STEP_CHANGE, data);
    });
  }

  checkCurrentDoc() {
    return new Promise((resolve, reject) => {
      let count = 0;
      const timer = setInterval(() => {
        if (this.currentDoc) {
          resolve();
          clearInterval(timer);
        } else {
          count++;
          console.log('checkCurrentDoc count', count);
          if (count > 20) {
            clearInterval(timer);
            console.error('CurrentDoc不存在');
            reject();
          }
        }
      }, 100);
    });
  }
  /**
   * 某些情况下需要等待 容器创建完毕，2s是一个最大阈值，2s内没等到创建完成则视为失败。
   * @param {string} cid 容器ID
   * @returns promise
   */
  checkDocByCid(cid) {
    return new Promise((resolve, reject) => {
      let count = 0;
      const timer = setInterval(() => {
        let doc = this.docMap.get(cid);
        if (doc) {
          resolve(doc);
          clearInterval(timer);
        } else {
          count++;
          console.log(`checkDoc ${cid} count`, count);
          if (count > 20) {
            clearInterval(timer);
            console.error(`doc ${cid} 不存在`);
            reject();
          }
        }
      }, 100);
    });
  }
  /**
   * 清空撤销重做数据
   */
  async resetUndoManagerData() {
    await this.checkCurrentDoc();
    this.currentDoc.resetUndoManagerData();
  }
  rotate(deg) {
    DocModuler.rotateDeg = deg;
    this.zoomReset();
  }
}
